import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { useToast } from '../ui/toast.jsx';
import { formatUtcInTimezone } from '../../lib/time.js';
import { api, ApiError } from '../../api/index.js';
import {
  LastRefresh, ExportButtons, SearchInput, applySearch,
  TtlBadge, useTableState, SortIcon,
} from '../../lib/table-utils.jsx';

/**
 * Locations — friendly-force tracking page (port of reference
 * `manuals/COP-microservice-integration/Locations/locations-page.jsx`).
 *
 * Reads layers from dataset "cop-ds-locations" (auto-created and fed by
 * AndroidIngestService on ad-cop-service). One layer per unit
 * (`cop-l-loc-{unitId}`), one object per terminal inside
 * (`loc-{terminalId}`), with historical track points stored on
 * `_androidSource.trail[]`.
 *
 * Read-only operator view.
 *
 * Polls /api/cop-layers every 10s through coord's cop-proxy.
 *
 * Adjustments vs reference:
 *   - useTimezone() → currentUser?.timezone prop
 *   - formatInTz()  → coord's formatUtcInTimezone()
 *   - onToast?.()   → coord's useToast().{success,error,info}
 *   - api.getCopLayers() / api.getCopLayer() → api.cop.* proxied
 *   - shadow-panel  → shadow-card
 *   - Wrapped in <PageShell>
 *   - Subtitle rewritten in English (coord UI = English only — see
 *     CLAUDE.md «UI strings follow the surface»).
 */

const POLL_MS = 10000;
const LOC_DATASET_ID = 'cop-ds-locations';

// Freshness window for the per-terminal TTL badge — "is this terminal
// still actively pinging?". 5 minutes is the typical Android upload
// cadence + retry buffer.
const ACTIVE_WINDOW_MS = 5 * 60 * 1000;

function unitIdFromLayerId(layerId) {
  return String(layerId || '').replace(/^cop-l-loc-/, '') || '(unknown)';
}

/**
 * Extract the canonical terminalId from an object ID. Handles two formats:
 *   1. New ping-per-object format:
 *      - loc-{terminalUuid}-{epochMs}    — single position recording
 *      - track-{terminalUuid}            — accumulated polyline of the trail
 *   2. Legacy aggregated format:
 *      - loc-{terminalUuid}              — one object per terminal, history
 *                                          in _androidSource.trail[]
 * The trailing -{epochMs} (≥10 digits) is stripped only if present.
 */
function terminalIdFromObjectId(objectId) {
  let id = String(objectId || '').replace(/^(loc|track)-/, '');
  id = id.replace(/-\d{10,}$/, '');
  return id || '(unknown)';
}

/**
 * Group objects of a unit's layer by terminalId. Returns one record per
 * unique terminal with the latest position recording + trail count.
 *   - `loc-*` objects → individual position pings; latest by `sentAt`
 *     becomes the "current position" shown in the table.
 *   - `track-*` objects → polyline geometries; not counted in trail.
 *   - Trail count = (# of loc-* points for this terminal − 1) +
 *                   (legacy _androidSource.trail.length on the latest).
 */
function summarizeTerminals(objects) {
  const byTerminal = new Map();
  for (const o of objects) {
    const tid = terminalIdFromObjectId(o.id);
    if (!byTerminal.has(tid)) byTerminal.set(tid, { tid, points: [], track: null });
    const bucket = byTerminal.get(tid);
    if (String(o.id || '').startsWith('track-')) {
      bucket.track = o;
    } else {
      bucket.points.push(o);
    }
  }
  const result = [];
  for (const { tid, points, track } of byTerminal.values()) {
    points.sort((a, b) => {
      const aTs = a._androidSource?.sentAt || '';
      const bTs = b._androidSource?.sentAt || '';
      return bTs.localeCompare(aTs);
    });
    const latest = points[0] || track;
    if (!latest) continue;
    const src = latest._androidSource || {};
    const props = latest.feature?.properties || {};
    // Read fields from the LATEST ping. android-ingest stores the
    // broadcast snapshot under `_track` (operator-canonical source);
    // geometry.lat/lon is the object root (also canonical); only as a
    // last resort do we fall back to feature.geometry.coordinates
    // (GeoJSON shape) for older inserts. Earlier code read
    // `feature.properties.{callsign,speed,heading}` and
    // `geometry.coordinates` — neither of which the ingest pipeline
    // populates today → all four columns rendered as «—».
    const trackSnap = latest._track || {};
    const geom = latest.geometry || {};
    const featCoords = latest.feature?.geometry?.coordinates || [];
    const lat = trackSnap.lat ?? geom.lat
        ?? (typeof featCoords[1] === 'number' ? featCoords[1] : null);
    const lon = trackSnap.lon ?? geom.lon
        ?? (typeof featCoords[0] === 'number' ? featCoords[0] : null);
    const legacyTrail = Array.isArray(src.trail) ? src.trail.length : 0;
    const trail = Math.max(0, points.length - 1) + legacyTrail;
    // Callsign — `_track.callsign` is the broadcast snapshot (operator-
    // canonical, freshest); feature.properties.uniqueDesignation /
    // op_unitName / operationalData.unitName are the milspec-derived
    // mirrors set by ad-cop-service's android-ingest. `props.callsign`
    // (what the earlier code read) is never populated by android-ingest.
    // v1.18+: per-snapshot biometrics block from the latest ping. Three
    // possible homes on the server side (different code paths stamp
    // different ones; pull from the freshest available):
    //   1. _track.biometrics — broadcast snapshot at moment of emit
    //      (canonical, freshest).
    //   2. _androidSource.biometrics — pass-through ingest payload mirror.
    //   3. operationalData.biometrics — milspec-derived federation mirror.
    // Empty {} or all-null values treated as «no biometrics».
    const bio = latest._track?.biometrics
            || latest._androidSource?.biometrics
            || latest.operationalData?.biometrics
            || null;
    const hasBio = !!(bio && typeof bio === 'object'
            && Object.keys(bio).some((k) => bio[k] != null));
    result.push({
      tid,
      callsign: trackSnap.callsign
          || props.uniqueDesignation
          || props.op_unitName
          || latest.operationalData?.unitName
          || '',
      lat,
      lon,
      speed: trackSnap.speedMps ?? null,
      heading: trackSnap.headingDeg ?? null,
      trail,
      sentAt: src.sentAt || null,
      biometrics: hasBio ? bio : null,
      _pointObjectCount: points.length,
      _legacyTrailLen: legacyTrail,
    });
  }
  result.sort((a, b) => a.tid.localeCompare(b.tid));
  return result;
}

function formatLatLon(lat, lon) {
  if (typeof lat !== 'number' || typeof lon !== 'number') return '—';
  const ns = lat >= 0 ? 'N' : 'S';
  const ew = lon >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(5)}° ${ns}  ${Math.abs(lon).toFixed(5)}° ${ew}`;
}

/** camelCase → snake_case. Used by the CSV exporter when streaming
 *  pass-through biometric fields whose original snake_case name was
 *  consumed by the server's snakeToCamel converter on ingest. */
function camelToSnake(s) {
  return s.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

export function CopLocationsPage({ currentUser }) {
  const toast = useToast();
  const tz = currentUser?.timezone || 'UTC';
  const isAdmin = currentUser?.role === 'admin';
  const formatDate = useCallback((iso) => iso ? formatUtcInTimezone(iso, tz) : '—', [tz]);

  const [layers, setLayers] = useState([]);
  const [objectsByLayer, setObjectsByLayer] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedLayerId, setExpandedLayerId] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [search, setSearch] = useState('');
  const [notInstalled, setNotInstalled] = useState(null);
  const [clearingLayerId, setClearingLayerId] = useState(null);

  const reload = useCallback(async () => {
    try {
      const all = await api.cop.getLayers();
      const list = all?.layers || all?.items || (Array.isArray(all) ? all : []);
      const items = list.filter((l) => l.datasetId === LOC_DATASET_ID);
      items.sort((a, b) => unitIdFromLayerId(a.id).localeCompare(unitIdFromLayerId(b.id)));
      setLayers(items);

      const objMap = {};
      await Promise.all(items.map(async (l) => {
        try {
          const res = await api.cop.getLayer(l.id);
          objMap[l.id] = res?.objects || [];
        } catch {
          objMap[l.id] = [];
        }
      }));
      setObjectsByLayer(objMap);
      setRefreshedAt(new Date());
      setNotInstalled(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setNotInstalled(err.payload || { reason: 'unknown' });
      } else {
        toast.error(`Locations reload failed: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      reload();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [reload]);

  /**
   * Clear the historical trail for a unit's layer, keeping only the latest
   * `loc-*` ping per terminal. All `track-*` polyline objects are removed.
   * Sequential DELETE calls (per object) — server requires admin role.
   */
  const handleClearTrack = useCallback(async (layer) => {
    const unitId = unitIdFromLayerId(layer.id);
    const objs = objectsByLayer[layer.id] || [];
    if (objs.length === 0) {
      toast.info(`Unit ${unitId}: layer is already empty`);
      return;
    }
    const latestLocByTerminal = new Map();
    for (const o of objs) {
      if (!String(o.id || '').startsWith('loc-')) continue;
      const tid = terminalIdFromObjectId(o.id);
      const sentAt = o._androidSource?.sentAt || '';
      const cur = latestLocByTerminal.get(tid);
      if (!cur || sentAt > (cur._androidSource?.sentAt || '')) {
        latestLocByTerminal.set(tid, o);
      }
    }
    const toKeep = new Set(Array.from(latestLocByTerminal.values()).map((o) => o.id));
    const toDelete = objs.filter((o) => !toKeep.has(o.id));
    if (toDelete.length === 0) {
      toast.info(`Unit ${unitId}: trail already clean (1 record per terminal)`);
      return;
    }
    if (!window.confirm(
      `Clear track for unit "${unitId}"?\n\n` +
      `Will delete ${toDelete.length} historical record(s), keeping the latest position per terminal.\n` +
      `Terminals affected: ${latestLocByTerminal.size}.\n` +
      `This is irreversible — Android terminals will re-populate trail on next ping.`
    )) return;
    setClearingLayerId(layer.id);
    let deleted = 0, failed = 0;
    try {
      for (const o of toDelete) {
        try {
          await api.cop.deleteLayerObject(layer.id, o.id);
          deleted++;
        } catch { failed++; }
      }
      const tail = failed > 0 ? ` (${failed} failed)` : '';
      toast.success(`Cleared ${deleted}/${toDelete.length} records for ${unitId}${tail}`);
      await reload();
    } finally {
      setClearingLayerId(null);
    }
  }, [objectsByLayer, toast, reload]);

  // Build per-terminal summaries per layer once — used by stats, search,
  // sort, export, and the inner expanded table.
  const terminalsByLayer = useMemo(() => {
    const out = {};
    for (const layer of layers) {
      out[layer.id] = summarizeTerminals(objectsByLayer[layer.id] || []);
    }
    return out;
  }, [layers, objectsByLayer]);

  // Flat list with one row per TERMINAL (not per object).
  const flatRows = useMemo(() => {
    const rows = [];
    for (const layer of layers) {
      const unitId = unitIdFromLayerId(layer.id);
      const terminals = terminalsByLayer[layer.id] || [];
      for (const t of terminals) {
        rows.push({
          unitId,
          layerId: layer.id,
          terminalId: t.tid,
          callsign: t.callsign,
          lat: t.lat,
          lon: t.lon,
          speed: t.speed,
          heading: t.heading,
          trail: t.trail,
          sentAt: t.sentAt,
          biometrics: t.biometrics,
          layerUpdatedAt: layer.updatedAt || null,
        });
      }
    }
    return rows;
  }, [layers, terminalsByLayer]);

  const searched = useMemo(
    () => applySearch(flatRows, search, ['unitId', 'terminalId', 'callsign']),
    [flatRows, search]
  );

  const groupedLayers = useMemo(() => {
    if (!search) return layers;
    const matchingUnits = new Set(searched.map((r) => r.layerId));
    return layers.filter((l) => matchingUnits.has(l.id));
  }, [layers, searched, search]);

  // For each unit (layer), filter its terminal-summary list down to the
  // terminals matched by the current search.
  const filteredTerminalsByLayer = useMemo(() => {
    if (!search) return terminalsByLayer;
    const matchingTerminalIds = new Set(searched.map((r) => `${r.layerId}::${r.terminalId}`));
    const out = {};
    for (const l of groupedLayers) {
      out[l.id] = (terminalsByLayer[l.id] || []).filter(
        (t) => matchingTerminalIds.has(`${l.id}::${t.tid}`)
      );
    }
    return out;
  }, [searched, terminalsByLayer, groupedLayers, search]);

  const layerSummaries = useMemo(() => groupedLayers.map((l) => {
    const terminals = filteredTerminalsByLayer[l.id] || [];
    const trailTotal = terminals.reduce(
      (s, t) => s + (t._pointObjectCount || 0) + (t._legacyTrailLen || 0),
      0
    );
    return {
      layer: l,
      unitId: unitIdFromLayerId(l.id),
      terminalCount: terminals.length,
      trailTotal,
      lastUpdate: l.updatedAt || '',
    };
  }), [groupedLayers, filteredTerminalsByLayer]);

  const { sortedRows, sortKey, sortDir, toggleSort } = useTableState(layerSummaries, {
    defaultSortKey: 'unitId',
    defaultSortDir: 'asc',
    pageSize: 100,
    sortAccessors: {
      unitId:        (r) => r.unitId,
      terminalCount: (r) => r.terminalCount,
      trailTotal:    (r) => r.trailTotal,
      lastUpdate:    (r) => r.lastUpdate,
    },
  });

  const stats = useMemo(() => {
    let terminals = 0;
    let totalPoints = 0;
    for (const l of layers) {
      const list = terminalsByLayer[l.id] || [];
      terminals += list.length;
      for (const t of list) {
        totalPoints += (t._pointObjectCount || 0) + (t._legacyTrailLen || 0);
      }
    }
    return { units: layers.length, terminals, totalPoints };
  }, [layers, terminalsByLayer]);

  // v1.19+: biometric columns are emitted in two passes —
  //   1. KNOWN curated columns (always emitted, stable CSV structure
  //      that downstream scripts depend on)
  //   2. UNKNOWN passthrough columns (discovered dynamically from
  //      whatever ad-tms-service has streamed in; alphabetically sorted)
  // This means a new Health Connect field arriving from Android (e.g.
  // vo2_max, hrv_rmssd_ms from Garmin / Galaxy Watch) shows up in the
  // CSV automatically, without a server or client code change. JSON
  // export dumps the row verbatim with the nested `biometrics` object
  // — known + unknown all in camelCase as the server stamped them.
  const exportColumns = useMemo(() => {
    const KNOWN = [
      { snake: 'hr_bpm',                 camel: 'hrBpm',                label: 'HR (bpm)' },
      { snake: 'spo2_pct',               camel: 'spo2Pct',              label: 'SpO2 (%)' },
      { snake: 'bp_sys_mmhg',            camel: 'bpSysMmhg',            label: 'BP sys (mmHg)' },
      { snake: 'bp_dia_mmhg',            camel: 'bpDiaMmhg',            label: 'BP dia (mmHg)' },
      { snake: 'body_temp_c',            camel: 'bodyTempC',            label: 'Body temp (°C)' },
      { snake: 'steps_today',            camel: 'stepsToday',           label: 'Steps today' },
      { snake: 'distance_m_today',       camel: 'distanceMToday',       label: 'Distance today (m)' },
      { snake: 'kcal_total_today',       camel: 'kcalTotalToday',       label: 'Calories today (kcal)' },
      { snake: 'sleep_duration_min',     camel: 'sleepDurationMin',     label: 'Sleep last night (min)' },
      { snake: 'bio_source',             camel: 'source',               label: 'Biometrics source', isStr: true },
    ];
    const knownCamels = new Set(KNOWN.map((c) => c.camel));

    // Discover passthrough camelCase keys across all currently-visible
    // rows. Server has already snake→camel'd them on ingest; we go back
    // to snake_case for the CSV header, matching the wire-contract.
    const extras = new Set();
    for (const row of searched) {
      const bio = row.biometrics;
      if (!bio || typeof bio !== 'object') continue;
      for (const k of Object.keys(bio)) {
        if (!knownCamels.has(k)) extras.add(k);
      }
    }

    const knownCols = KNOWN.map(({ snake, camel, label, isStr }) => ({
      key: snake,
      label,
      get: (r) => {
        const v = r.biometrics?.[camel];
        if (isStr) return v || '';
        if (Number.isFinite(v)) return v;
        if (typeof v === 'string' && v.length > 0) return v;
        return '';
      },
    }));

    const extraCols = Array.from(extras).sort().map((camel) => ({
      key: camelToSnake(camel),
      label: camelToSnake(camel),
      get: (r) => {
        const v = r.biometrics?.[camel];
        if (Number.isFinite(v)) return v;
        if (typeof v === 'string' && v.length > 0) return v;
        if (typeof v === 'boolean') return v ? 'true' : 'false';
        return '';
      },
    }));

    return [
      { key: 'unitId',     label: 'Unit' },
      { key: 'terminalId', label: 'Terminal' },
      { key: 'callsign',   label: 'Callsign' },
      { key: 'lat',        label: 'Lat' },
      { key: 'lon',        label: 'Lon' },
      { key: 'speed',      label: 'Speed (m/s)' },
      { key: 'heading',    label: 'Heading (°)' },
      { key: 'trail',      label: 'Trail pts' },
      { key: 'sentAt',     label: 'Last seen (ISO)' },
      ...knownCols,
      ...extraCols,
    ];
  }, [searched]);

  return (
    <PageShell
      icon="fa-location-crosshairs"
      title="Locations"
      subtitle="Friendly-force tracking — terminal positions and tracks ingested via ad-tms-service. Read-only operator view; data is auto-cleaned by the server's TTL janitor."
      actions={
        notInstalled ? null : (
          <div className="flex items-center gap-2">
            <LastRefresh ts={refreshedAt} />
            <ExportButtons
              getRows={() => searched}
              columns={exportColumns}
              filenameBase="locations"
              disabled={searched.length === 0}
            />
          </div>
        )
      }
    >
      {notInstalled ? (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <i className="fa-sharp fa-solid fa-triangle-exclamation mr-2" />
          COP ADMS service is not reachable
          {notInstalled.reason ? ` (${notInstalled.reason})` : ''}. Open
          <span className="font-medium"> COP ADMS → Datasets</span> for diagnostics.
        </div>
      ) : loading ? (
        <div className="card p-10 text-center text-sm text-mist">Loading locations…</div>
      ) : (
        <>
          <div className="mb-5 grid grid-cols-3 gap-3">
            <Stat label="Units"     value={stats.units} />
            <Stat label="Terminals" value={stats.terminals}   tone="emerald" />
            <Stat label="Track pts" value={stats.totalPoints} tone="sky" />
          </div>

          <div className="mb-4 max-w-md">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search by unit / terminal / callsign…"
            />
          </div>

          <div className="overflow-x-auto rounded-2xl border border-line bg-white shadow-card">
            <table className="w-full text-sm">
              <thead className="border-b border-line bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-mist">
                <tr>
                  <th className="px-3 py-2.5 w-10"></th>
                  <SortableTh label="Unit"        sortKey={sortKey} dir={sortDir} colKey="unitId"        onSort={toggleSort} className="px-3 py-2.5" />
                  <SortableTh label="Terminals"   sortKey={sortKey} dir={sortDir} colKey="terminalCount" onSort={toggleSort} className="px-3 py-2.5 w-32 text-right" />
                  <SortableTh label="Track pts"   sortKey={sortKey} dir={sortDir} colKey="trailTotal"    onSort={toggleSort} className="px-3 py-2.5 w-32 text-right" />
                  <SortableTh label="Last update" sortKey={sortKey} dir={sortDir} colKey="lastUpdate"    onSort={toggleSort} className="px-3 py-2.5 w-44" />
                  {isAdmin && <th className="px-3 py-2.5 w-32 text-center whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-mist">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sortedRows.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 6 : 5} className="p-8 text-center text-mist">
                    {search ? 'No units / terminals match the search.' : 'No location data yet. Waiting for terminal pings via ad-tms-service.'}
                  </td></tr>
                ) : sortedRows.map(({ layer, unitId, terminalCount, trailTotal, lastUpdate }) => {
                  const terminals = filteredTerminalsByLayer[layer.id] || [];
                  const expanded = expandedLayerId === layer.id;
                  return (
                    <Fragment key={layer.id}>
                      <tr
                        className="border-b border-line cursor-pointer hover:bg-slate-50"
                        onClick={() => setExpandedLayerId(expanded ? null : layer.id)}
                      >
                        <td className="px-3 py-2">
                          <i className={`fa-sharp fa-solid ${expanded ? 'fa-chevron-down' : 'fa-chevron-right'} text-xs text-mist`} />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs font-semibold text-ink">{unitId}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-xs">{terminalCount}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-xs text-mist">{trailTotal}</td>
                        <td className="px-3 py-2 text-xs text-mist tabular-nums">{lastUpdate ? formatDate(lastUpdate) : '—'}</td>
                        {isAdmin && (
                          <td className="px-3 py-2 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="whitespace-nowrap rounded-lg border border-line bg-white px-2 py-1 text-[11px] font-medium text-mist hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200 disabled:opacity-40 disabled:cursor-not-allowed"
                              onClick={() => handleClearTrack(layer)}
                              disabled={clearingLayerId === layer.id || trailTotal <= terminalCount}
                              title={trailTotal <= terminalCount
                                ? 'Trail already clean — only the latest record per terminal'
                                : `Clear historical records — keep only the latest position per terminal (${terminalCount} terminal(s) → ${terminalCount} record(s))`}
                            >
                              <i className={`fa-sharp fa-solid fa-broom mr-1 ${clearingLayerId === layer.id ? 'animate-pulse' : ''}`} />
                              {clearingLayerId === layer.id ? 'Clearing…' : 'Clear track'}
                            </button>
                          </td>
                        )}
                      </tr>
                      {expanded && (
                        <tr className="border-b border-line bg-slate-50/40">
                          <td colSpan={isAdmin ? 6 : 5} className="px-6 py-3">
                            {terminals.length === 0 ? (
                              <div className="text-xs text-mist">No terminals reported yet.</div>
                            ) : (
                              <table className="w-full text-xs">
                                <thead className="text-mist text-[10px] uppercase tracking-wider">
                                  <tr>
                                    <th className="text-left   py-1.5 pr-3 whitespace-nowrap">Terminal</th>
                                    <th className="text-center py-1.5 pr-3 whitespace-nowrap">Callsign</th>
                                    <th className="text-left   py-1.5 pr-3 whitespace-nowrap">Current position</th>
                                    <th className="text-center py-1.5 pr-3 whitespace-nowrap">Speed</th>
                                    <th className="text-center py-1.5 pr-3 whitespace-nowrap">Heading</th>
                                    <th className="text-center py-1.5 pr-3 whitespace-nowrap">Trail</th>
                                    <th className="text-left   py-1.5 pr-3 whitespace-nowrap">Last seen</th>
                                    <th className="text-left   py-1.5 pr-3 whitespace-nowrap">Status</th>
                                    <th className="text-left   py-1.5 pr-3 whitespace-nowrap">Vitals</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {terminals.map((t) => (
                                    <tr key={t.tid} className="border-t border-line/60">
                                      <td className="py-1.5 pr-3 font-mono">{t.tid}</td>
                                      <td className="py-1.5 pr-3 text-center">
                                        {/* Callsign + BIOMETRICS badge stacked vertically — badge
                                            shows only when latest ping carries any non-null
                                            biometric field. Was experimentally moved into the
                                            Vitals column in an earlier round; operator preferred
                                            the original placement here. */}
                                        <div className="inline-flex flex-col items-center gap-0.5 leading-tight">
                                          <span>{t.callsign || '—'}</span>
                                          {t.biometrics ? <BiometricsBadge /> : null}
                                        </div>
                                      </td>
                                      <td className="py-1.5 pr-3 font-mono text-[11px] tabular-nums">{formatLatLon(t.lat, t.lon)}</td>
                                      <td className="py-1.5 pr-3 text-center tabular-nums">
                                        {t.speed != null ? (
                                          <div className="inline-flex flex-col items-center gap-0.5 leading-tight">
                                            <span>
                                              {t.speed.toFixed(1)} m/s — {(t.speed * 3.6).toFixed(1)} km/h
                                            </span>
                                            {/* Badge reads the ROUNDED speed (parseFloat of the displayed
                                                "0.0") so GPS jitter values like 2.1e-7 — which print as
                                                "0.0 m/s" — also classify as `stopping`. Without this the
                                                badge would say `moving` for sub-cm/s noise on a still
                                                terminal. */}
                                            <MotionBadge speed={Number(t.speed.toFixed(1))} />
                                          </div>
                                        ) : '—'}
                                      </td>
                                      <td className="py-1.5 pr-3 text-center tabular-nums">{t.heading != null ? `${Math.round(t.heading)}°` : '—'}</td>
                                      <td className="py-1.5 pr-3 text-center tabular-nums">{t.trail}</td>
                                      <td className="py-1.5 pr-3 tabular-nums text-mist">{t.sentAt ? formatDate(t.sentAt) : '—'}</td>
                                      <td className="py-1.5 pr-3"><TtlBadge lastUpdate={t.sentAt} ttlMs={ACTIVE_WINDOW_MS} /></td>
                                      <td className="py-1.5 pr-3"><VitalsCell bio={t.biometrics} /></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageShell>
  );
}

function SortableTh({ label, sortKey, dir, colKey, onSort, className = '' }) {
  const active = sortKey === colKey;
  return (
    <th className={`${className} cursor-pointer select-none whitespace-nowrap`} onClick={() => onSort(colKey)}>
      <span>{label}</span>
      <SortIcon active={active} dir={dir} />
    </th>
  );
}

/**
 * MotionBadge — compact pill alongside the Speed value.
 *   • speed > 0 → emerald «moving»
 *   • speed === 0 → slate «stopping»
 *   • null/undefined → not rendered (caller already shows «—»)
 */
function MotionBadge({ speed }) {
  if (typeof speed !== 'number') return null;
  const moving = speed > 0;
  const cls = moving
    ? 'bg-emerald-100 text-emerald-700'
    : 'bg-slate-100 text-slate-600';
  return (
    <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${cls}`}>
      {moving ? 'moving' : 'stopping'}
    </span>
  );
}

/**
 * VitalsCell — compact Vitals chip rendered inside the expanded terminal
 * row. Matches the COP map popup's color thresholds so a glance at the
 * table and a popup-click answer the same question. Renders only fields
 * actually present (contract: present-or-absent, no null/0 placeholders).
 *
 *   HR        — < 60 blue / 60-100 green / 101-130 amber / > 130 red
 *   SpO₂      — ≥ 95 green / 90-94 amber / < 90 red
 *   BP        — 90-140 sys / 60-90 dia green; outside amber; far outside red
 *   Temp (°C) — 36.0-37.5 green / 37.5-38 amber / < 35 or > 38 red
 *   Sleep h   — ≥ 6 green / 4-6 amber / < 4 red
 *   Activity (steps / distance / calories) — informational, no color
 *
 * v1.19+: stays curator — unknown pass-through fields (vo2_max, hrv_*,
 * weight_kg, etc) are intentionally NOT rendered here. They surface in
 * CSV/JSON export automatically and the operator extends VitalsCell
 * when a new threshold is worth surfacing.
 */
function VitalsCell({ bio }) {
  if (!bio) return <span className="text-mist text-[10px]">—</span>;
  const chips = [];
  if (Number.isFinite(bio.hrBpm)) {
    const cls = bio.hrBpm < 60 ? 'bg-blue-50 text-blue-700 border-blue-200'
              : bio.hrBpm <= 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : bio.hrBpm <= 130 ? 'bg-amber-50 text-amber-700 border-amber-200'
              : 'bg-rose-50 text-rose-700 border-rose-200';
    chips.push(
      <span key="hr" className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${cls} whitespace-nowrap`} title="Heart rate (bpm)">
        ♥ {bio.hrBpm}
      </span>
    );
  }
  if (Number.isFinite(bio.spo2Pct)) {
    const cls = bio.spo2Pct >= 95 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : bio.spo2Pct >= 90 ? 'bg-amber-50 text-amber-700 border-amber-200'
              : 'bg-rose-50 text-rose-700 border-rose-200';
    chips.push(
      <span key="spo2" className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${cls} whitespace-nowrap`} title="SpO₂ (%)">
        SpO₂ {bio.spo2Pct}%
      </span>
    );
  }
  if (Number.isFinite(bio.bpSysMmhg) && Number.isFinite(bio.bpDiaMmhg)) {
    const critical = bio.bpSysMmhg < 80 || bio.bpSysMmhg > 180 || bio.bpDiaMmhg < 50 || bio.bpDiaMmhg > 110;
    const alarm    = bio.bpSysMmhg < 90 || bio.bpSysMmhg > 140 || bio.bpDiaMmhg < 60 || bio.bpDiaMmhg > 90;
    const cls = critical ? 'bg-rose-50 text-rose-700 border-rose-200'
              : alarm    ? 'bg-amber-50 text-amber-700 border-amber-200'
              : 'bg-emerald-50 text-emerald-700 border-emerald-200';
    chips.push(
      <span key="bp" className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${cls} whitespace-nowrap`} title="Blood pressure (mmHg)">
        BP {bio.bpSysMmhg}/{bio.bpDiaMmhg}
      </span>
    );
  }
  if (Number.isFinite(bio.bodyTempC)) {
    const cls = bio.bodyTempC < 35 ? 'bg-rose-50 text-rose-700 border-rose-200'
              : bio.bodyTempC <= 37.5 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : bio.bodyTempC <= 38 ? 'bg-amber-50 text-amber-700 border-amber-200'
              : 'bg-rose-50 text-rose-700 border-rose-200';
    chips.push(
      <span key="t" className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${cls} whitespace-nowrap`} title="Body temperature (°C)">
        {bio.bodyTempC.toFixed(1)}°C
      </span>
    );
  }
  if (Number.isFinite(bio.sleepDurationMin)) {
    // Wire-contract §2.4 — minutes, not hours. Thresholds: <240 (<4h),
    // 240-360 (4-6h), ≥360 (≥6h). Display in "Xh Ym" form for the chip.
    const cls = bio.sleepDurationMin < 240 ? 'bg-rose-50 text-rose-700 border-rose-200'
              : bio.sleepDurationMin < 360 ? 'bg-amber-50 text-amber-700 border-amber-200'
              : 'bg-emerald-50 text-emerald-700 border-emerald-200';
    const hh = Math.floor(bio.sleepDurationMin / 60);
    const mm = Math.round(bio.sleepDurationMin % 60);
    chips.push(
      <span key="sl" className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${cls} whitespace-nowrap`} title="Sleep last night">
        💤 {hh}h {mm}m
      </span>
    );
  }
  // Activity — three independent chips (steps / distance / calories),
  // each with its own glyph so the operator parses them by glance.
  // Each is informational, no color thresholds.
  const actCls = 'rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 whitespace-nowrap';
  if (Number.isFinite(bio.stepsToday)) {
    chips.push(
      <span key="steps" className={actCls} title="Steps today">
        👣 {bio.stepsToday.toLocaleString()}
      </span>
    );
  }
  if (Number.isFinite(bio.distanceMToday)) {
    chips.push(
      <span key="dist" className={actCls} title="Distance today">
        📏 {(bio.distanceMToday / 1000).toFixed(2)} km
      </span>
    );
  }
  if (Number.isFinite(bio.kcalTotalToday)) {
    chips.push(
      <span key="kcal" className={actCls} title="Calories today">
        🔥 {bio.kcalTotalToday} kcal
      </span>
    );
  }
  if (chips.length === 0) {
    return (
      <span className="text-mist text-[10px]" title={bio.source || ''}>{bio.source || '—'}</span>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {chips}
    </div>
  );
}

/**
 * BiometricsBadge — indigo pill flagging that the terminal's latest ping
 * carries Health Connect biometric data (HR / SpO₂ / BP / temp / steps /
 * distance / calories / sleep). v1.18+.
 */
function BiometricsBadge() {
  return (
    <span
      className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-indigo-700"
      title="Latest ping carries Health Connect biometrics"
    >
      Biometrics
    </span>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === 'emerald' ? 'text-emerald-700'
            : tone === 'sky'     ? 'text-sky-700'
            : 'text-ink';
  return (
    <div className="rounded-2xl border border-line bg-white px-4 py-3 shadow-card">
      <div className="text-xs uppercase tracking-wider text-mist">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
